import { useRef } from "react";
import { ALBUM_LAYOUTS, CAROUSEL_MAX_IMAGES, CAROUSEL_RATIOS, findAlbumLayout } from "../../FdEditor/components/SubFdElements/weget/weget-image-module/albumLayouts";
import { MoveButtons } from "../../FdEditor/components/SubFdElements/weget/weget-image-module/WegetImageModuleForm";

// 배치별 최대 장수. 서버 PostsUseCase 와 같아야 한다 (앨범 2·4·6장, 캐러셀 1~10장)
const IMAGE_LAYOUTS = [
    { value: 'SINGLE', label: '한 장', max: 1 },
    { value: 'ALBUM', label: '앨범', max: 6 },
    { value: 'CAROUSEL', label: '캐러셀', max: CAROUSEL_MAX_IMAGES },
];

const SEGMENT = (active) => `rounded-lg px-3 py-1.5 text-[13px] font-semibold ring-1 transition ${active ? 'bg-gray-900 text-white ring-gray-900' : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'}`;

/** 저장할 수 없는 이유 (없으면 null). 사진이 없으면 저장할 때 모듈째 빠지므로 null */
export function imageModuleError(module) {
    const count = module.urls.length;
    if (!count) {
        return null;
    }
    if (module.layout === 'SINGLE' && count > 1) {
        return `한 장 배치는 사진 1장만 넣을 수 있어요. 지금 ${count}장이에요.`;
    }
    if (module.layout === 'ALBUM' && !ALBUM_LAYOUTS[count]) {
        return `앨범은 사진 2·4·6장으로 만들어요. 지금 ${count}장이에요.`;
    }
    return null;
}

/** 저장할 모양으로 (배치에 필요한 값만) */
export function toImageModuleBody(module) {
    return {
        type: 'IMAGE',
        layout: module.layout,
        urls: module.urls,
        ...(module.layout === 'ALBUM' ? { albumLayout: findAlbumLayout(module.urls.length, module.albumLayout).value } : {}),
        ...(module.layout === 'CAROUSEL' ? { ratio: module.ratio } : {}),
    };
}

/** 이미지 모듈: 배치(한 장·앨범·캐러셀) + 사진 추가·삭제·순서. uploadImages(files) → url 배열 */
export function FgPostImageModule({ module, onChange, uploadImages }) {
    const fileInputRef = useRef(null);
    const { layout, urls } = module;
    const max = IMAGE_LAYOUTS.find(l => l.value === layout).max;
    const albumLayout = layout === 'ALBUM' && ALBUM_LAYOUTS[urls.length] ? findAlbumLayout(urls.length, module.albumLayout) : null;
    const error = imageModuleError(module);

    const handleSelectFiles = async (e) => {
        const files = [...(e.target.files || [])].slice(0, max - urls.length);
        e.target.value = '';
        const added = await uploadImages(files);
        if (added.length) {
            onChange({ urls: [...urls, ...added] });
        }
    }

    const moveImage = (from, to) => {
        const next = [...urls];
        next.splice(to, 0, next.splice(from, 1)[0]);
        onChange({ urls: next });
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-1.5">
                {IMAGE_LAYOUTS.map(l => (
                    <button key={l.value} type="button" onClick={() => onChange({ layout: l.value })} aria-pressed={layout === l.value} className={SEGMENT(layout === l.value)}>{l.label}</button>
                ))}
                <span className="ml-1 text-xs text-gray-400">
                    {layout === 'SINGLE' && '사진 1장을 크게'}
                    {layout === 'ALBUM' && '2·4·6장을 한 화면에'}
                    {layout === 'CAROUSEL' && `옆으로 넘기기 (최대 ${CAROUSEL_MAX_IMAGES}장)`}
                </span>
            </div>

            {layout === 'CAROUSEL' &&
                <div className="flex flex-wrap gap-1.5">
                    {CAROUSEL_RATIOS.map(r => (
                        <button key={r.value} type="button" onClick={() => onChange({ ratio: r.value })} aria-pressed={module.ratio === r.value} className={SEGMENT(module.ratio === r.value)}>{r.label}</button>
                    ))}
                </div>
            }

            {albumLayout && ALBUM_LAYOUTS[urls.length].length > 1 &&
                <div className="flex flex-wrap gap-1.5">
                    {ALBUM_LAYOUTS[urls.length].map(l => (
                        <button key={l.value} type="button" onClick={() => onChange({ albumLayout: l.value })} aria-pressed={albumLayout.value === l.value} className={SEGMENT(albumLayout.value === l.value)}>{l.label}</button>
                    ))}
                </div>
            }

            {/* 사진: 순서대로 (앨범 배치 순서 = 이 순서) */}
            <div className="grid grid-cols-3 gap-2 min-[600px]:grid-cols-5">
                {urls.map((url, i) => (
                    <div key={`${url}-${i}`} className="relative aspect-square overflow-hidden rounded-lg bg-gray-100">
                        <img src={url} alt="" className="absolute inset-0 h-full w-full object-cover" />
                        <span className="absolute left-1 top-1 rounded bg-black/50 px-1.5 text-[11px] font-bold text-white">{i + 1}</span>
                        <button type="button" onClick={() => onChange({ urls: urls.filter((_, j) => j !== i) })} aria-label={`${i + 1}번 사진 빼기`} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white">✕</button>
                        <MoveButtons index={i} last={urls.length - 1} onMove={moveImage} />
                    </div>
                ))}
                {urls.length < max &&
                    <button type="button" onClick={() => fileInputRef.current.click()} className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600">
                        <span className="text-xl leading-none">+</span>
                        <span className="text-[11px] font-semibold">사진 추가</span>
                    </button>
                }
            </div>

            {error && <p className="text-xs font-semibold text-[var(--redColor1)]">{error}</p>}

            {/* 앨범 배치 미리보기 */}
            {albumLayout &&
                <div className="max-w-[280px]">
                    <p className="mb-1 text-[11px] font-semibold text-gray-400">미리보기 · {albumLayout.label}</p>
                    <div className={`${albumLayout.container} gap-[2px] overflow-hidden rounded-lg`}>
                        {urls.map((url, i) => (
                            <div key={i} className={`relative bg-gray-100 ${albumLayout.cells?.[i] || albumLayout.cell}`}>
                                <img src={url} alt="" className="absolute inset-0 h-full w-full object-cover" />
                            </div>
                        ))}
                    </div>
                </div>
            }

            <input ref={fileInputRef} type="file" accept="image/*" multiple={max > 1} className="hidden" onChange={handleSelectFiles} />
        </div>
    );
}
